import { useLoaderData } from 'react-router'
import { useRef, useEffect } from 'react'
import type { CSSProperties } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import {
  Route,
  Puzzle,
  ArrowUpRight,
  Grid3x3,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react'

import { Button, Stack, TabAccordion } from '~/components-v2'
import type { TabAccordionItem } from '~/components-v2'
import styles from './style.css?url'
import semanticDiagramStyles from './semantic-diagram.css?url'
import tabAccordionStyles from '~/components-v2/tab-accordion/tab-accordion.css?url'
import carouselRailStyles from '~/components-v2/carousel-rail/carousel-rail.css?url'
import carouselButtonNextStyles from '~/components-v2/carousel-button-next/carousel-button-next.css?url'
import heroStyles from '~/components-v2/hero/hero.css?url'
import linkCtaNextStyles from '~/components-v2/link-cta-next/link-cta-next.css?url'
import quoteStyles from '~/components/quote-section/quote-section.css?url'
import videoSliderStyles from '~/components-v2/video-slider/video-slider.css?url'

import {
  CustomersMarquee,
  customersMarqueeLinks,
} from '~/components/shared/customers-marquee'
import { QuoteCarousel } from '~/components/quote-section/quote-carousel'
import {
  QuoteSynthesiaModeling,
  QuoteGuitarCenter,
  QuoteBuzzfeed3,
  QuoteCheckr,
  QuoteHeidi,
  QuoteCheckr2,
  QuoteBrevo,
  QuoteCribl3,
  QuoteUscreen4,
  QuoteSynthesia2,
} from '~/config/quotes'
import {
  CallOutList,
  CallOutItem,
  CallOutText,
  CallOutIllustration,
} from '~/components-v2/callout-section/callout-section'

import { getBlogPostBySlug } from '~/services/contentful'
import { getContentfulRouteModules } from '~/services/contentful/route-modules.server'

import {
  PostCollection,
  postCollectionLinks,
} from '~/components-v2/post-collection/post-collection'
import { contentfulSectionLinks } from '~/sections'

if (typeof window !== 'undefined') {
  gsap.registerPlugin(useGSAP)
  gsap.registerPlugin(ScrollTrigger)
}

function UpDownArrowsIcon({
  className = 'semantic-diagram__arrows',
  showPulses = false,
  dotColor = 'pink',
  pulseOffset = 0,
}: {
  className?: string
  showPulses?: boolean
  dotColor?: 'pink' | 'dark'
  pulseOffset?: number
}) {
  return (
    <svg
      className={className}
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4.37378 0.864014V23.0032"
        stroke="#4D122C"
        strokeWidth="1.72795"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.864014 19.2233L4.3737 24.0835L7.88417 19.2233"
        stroke="#4D122C"
        strokeWidth="1.72795"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20.574 24.0837V1.94373"
        stroke="#4D122C"
        strokeWidth="1.72795"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.0642 5.72353L20.5739 0.864014L24.0844 5.72353"
        stroke="#4D122C"
        strokeWidth="1.72795"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {showPulses && (
        <>
          <circle
            className={`semantic-diagram__arrow-dot semantic-diagram__arrow-dot--down semantic-diagram__arrow-dot--${dotColor}`}
            cx="4.37"
            cy="2"
            r="2.5"
            style={{ animationDelay: `${pulseOffset}s` }}
          />
          <circle
            className={`semantic-diagram__arrow-dot semantic-diagram__arrow-dot--up semantic-diagram__arrow-dot--${dotColor}`}
            cx="20.57"
            cy="22"
            r="2.5"
            style={{ animationDelay: `${pulseOffset + 1}s` }}
          />
        </>
      )}
    </svg>
  )
}

function MobileArc() {
  return (
    <div className="semantic-diagram-mobile__arc" aria-hidden="true">
      <div className="semantic-diagram-mobile__arc-circle" />
    </div>
  )
}

const TRUST_ROWS = [
  {
    id: 'flexible',
    label: 'Flexible',
    Icon: Route,
    iconSrc: '/images/pages/semantic-layer/icons/flexible.png',
    description: (
      <>
        Explore first, then{' '}
        <a href="/blog/just-in-time-data-modeling">model what matters</a>. Build
        from the browser, in code, or with AI. When you've created something
        valuable, promote it safely - branch, review, evaluate, and merge its
        definition into the shared model.
      </>
    ),
  },
  {
    id: 'interoperable',
    label: 'Interoperable',
    Icon: Puzzle,
    iconSrc: '/images/pages/semantic-layer/icons/interoperable.png',
    description: (
      <>
        Bi-directional integrations with other semantic layers, such as dbt,
        Snowflake Semantic Views, and Databricks Unity Catalog. Written in YAML,
        committed to open standards (Apache Ossie), and portable by design.
      </>
    ),
  },
  {
    id: 'extensible',
    label: 'Extensible',
    Icon: ArrowUpRight,
    iconSrc: '/images/pages/semantic-layer/icons/extensible.png',
    description: (
      <>
        Handle exceptions without duplication or workarounds. Extend the model
        at any grain with custom logic, AI settings, display defaults, and more.
        When a core definition changes, the additive logic updates
        automatically.
      </>
    ),
  },
  {
    id: 'comprehensive',
    label: 'Comprehensive',
    Icon: Grid3x3,
    iconSrc: '/images/pages/semantic-layer/icons/comprehensive.png',
    description: (
      <>
        Every metric, join path, and edge case can be defined once. AI context
        teaches agents what your best experts already know.
      </>
    ),
  },
  {
    id: 'governable',
    label: 'Governable',
    Icon: ShieldCheck,
    iconSrc: '/images/pages/semantic-layer/icons/governable.png',
    description: (
      <>
        Every query is compiled from the model, enforcing the same definitions,
        permissions, and row-level filters.
      </>
    ),
  },
] as const

const MODEL_AS_YOU_GO_ITEMS: TabAccordionItem[] = [
  {
    id: 'define-logic',
    label: 'Define logic multiple ways',
    description:
      'Metrics and metadata can be created through SQL, point-and-click, and Excel-style formulas. Or you can ask the Agent to build it. This lets you experiment quickly while still contributing to a governed semantic model.',
    screenshot: '/images/pages/semantic-layer/logic.webp',
    screenshotAlt:
      'SQL, point-and-click, Excel formulas, and the Agent all feed the same governed model definition',
  },
  {
    id: 'analyze-first',
    label: 'Analyze first, model later',
    description: (
      <>
        Users can explore modeled and unmodeled data in a workbook without
        impacting the{' '}
        <a
          href="https://docs.omni.co/modeling"
          target="_blank"
          rel="noopener noreferrer"
        >
          shared model
        </a>
        . Create multiple tabs of queries, charts, spreadsheets, or pivot
        tables, and follow the question wherever it leads.
      </>
    ),
    screenshot: '/images/pages/semantic-layer/analyze-first.webp',
    screenshotAlt:
      'SQL, point-and-click, Excel formulas, and the Agent all feed the same governed model definition',
  },
  {
    id: 'model-faster-ai',
    label: 'Model faster with AI',
    description: (
      <>
        Omni's{' '}
        <a href="/blog/announcing-omnis-modeling-agent">Modeling Agent</a> helps
        you build and maintain the semantic model. Point it at your tables and
        it can define views, relationships, dimensions, and measures from your
        database schema.
      </>
    ),
    screenshot: '/images/pages/semantic-layer/model-faster.webp',
    screenshotAlt:
      'SQL, point-and-click, Excel formulas, and the Agent all feed the same governed model definition',
  },
  {
    id: 'analytics-like-software',
    label: 'Build analytics like software',
    description: (
      <>
        The model has a{' '}
        <a href="/blog/why-the-software-development-lifecycle-is-critical-for-bi">
          software development lifecycle
        </a>
        , and it's the one your engineers already use. Branch mode isolates a
        change from production. Git keeps every version and author. Review
        changes, then merge. Your model{' '}
        <a href="/blog/managing-changes-with-omnis-sdlc-controls">
          ships the way code does
        </a>
        .
        <br />
        <br />
        The{' '}
        <a
          href="https://docs.omni.co/modeling/develop/content-validator"
          target="_blank"
          rel="noopener noreferrer"
        >
          Content Validator
        </a>{' '}
        identifies and bulk-fixes broken downstream content. Schema refreshes
        keep the model in sync with your warehouse. And the API runs these
        workflows programmatically.
      </>
    ),
    screenshot: '/images/pages/semantic-layer/build-analytics.webp',
    screenshotAlt:
      'SQL, point-and-click, Excel formulas, and the Agent all feed the same governed model definition',
  },
  {
    id: 'manage-at-scale',
    label: 'Manage AI analytics at scale',
    description: (
      <>
        <a href="/blog/introducing-ai-hub">AI Hub</a> gives your team one place
        to observe how AI is performing, improve what needs fixing, and validate
        that it's working.
      </>
    ),
    screenshot: '/images/pages/semantic-layer/ai-hub-animated.svg',
    screenshotAlt: 'AI Hub dashboard showing AI performance and validation',
  },
  {
    id: 'test-your-agent',
    label: 'Put your agent to the test',
    description: (
      <>
        <a href="/blog/run-your-agent-like-a-data-product-with-ai-evals">
          Evals run reusable prompt sets
        </a>{' '}
        against a branch, and a built-in judge scores each answer, so you can
        observe how AI responds, and validate changes before you merge.
      </>
    ),
    screenshot: '/images/pages/semantic-layer/test-agents.webp',
    screenshotAlt:
      'SQL, point-and-click, Excel formulas, and the Agent all feed the same governed model definition',
  },
]

const TEACH_AI_ITEMS: TabAccordionItem[] = [
  {
    id: 'context-compounds',
    label: 'Context compounds as your business changes',
    description:
      'Omni monitors AI usage and offers Suggestions on how to improve your model. Evals allow testing in isolation before you deploy changes. As the model absorbs more context, the team fields fewer ad hoc questions and shifts to higher-value work.',
    screenshotContent: (
      <img
        className="teach-ai-screenshot-image"
        src="/images/pages/semantic-layer/teach-ai-your-1.webp"
        alt=""
      />
    ),
    screenshotLabel: 'Context compounds as your business changes',
  },
  {
    id: 'curate-topics',
    label: 'Curate datasets with Topics',
    description: (
      <>
        Group trusted business logic into reusable{' '}
        <a href="/blog/teach-ai-to-understand-your-business-with-omni-topics">
          Topics
        </a>{' '}
        so teams can work from curated datasets instead of recreating
        definitions across dashboards, spreadsheets, and SQL.
      </>
    ),
    screenshotContent: (
      <img
        className="teach-ai-screenshot-image"
        src="/images/pages/semantic-layer/model-topics.webp"
        alt=""
      />
    ),
    screenshotLabel: 'Curated datasets with Topics',
  },
  {
    id: 'ai-context',
    label: 'AI context as first-class parameters',
    description:
      'Capture the business context your team uses every day and make it available to AI as part of the model, so responses stay relevant, governed, and useful across every surface.',
    screenshotContent: (
      <img
        className="teach-ai-screenshot-image"
        src="/images/pages/semantic-layer/ai-context-field-table.webp"
        alt=""
      />
    ),
    screenshotLabel: 'AI context as first-class parameters',
  },
]

const AI_CONTEXT_POST_SLUGS = [
  'what-makes-a-semantic-layer-good',
  'why-text-to-sql-fails',
  'why-ai-needs-a-semantic-model',
]

const title = `The semantic layer built for BI and AI`
const subTitle = "Powered by Omni's semantic model."
const description = `Metrics, joins, and context in one platform. Governance for your team. Guardrails for your agents.`

export const loader = async () => {
  const [posts, routeModules] = await Promise.all([
    Promise.all(AI_CONTEXT_POST_SLUGS.map((slug) => getBlogPostBySlug(slug))),
    getContentfulRouteModules('/ai'),
  ])

  return {
    title,
    subTitle,
    description,
    posts: posts.filter(Boolean),
    routeModules,
  }
}
export { meta } from '~/utils'
export const links = () => [
  { rel: 'stylesheet', href: carouselRailStyles },
  { rel: 'stylesheet', href: carouselButtonNextStyles },
  { rel: 'stylesheet', href: heroStyles },
  { rel: 'stylesheet', href: linkCtaNextStyles },
  { rel: 'stylesheet', href: quoteStyles },
  { rel: 'stylesheet', href: videoSliderStyles },
  { rel: 'stylesheet', href: tabAccordionStyles },
  ...contentfulSectionLinks(),
  ...postCollectionLinks(),
  ...customersMarqueeLinks(),
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: semanticDiagramStyles },
]

export default function () {
  const { title, posts } = useLoaderData<typeof loader>()

  const governanceSectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const section = governanceSectionRef.current

    if (!section) return

    const rows = Array.from(
      section.querySelectorAll<HTMLElement>('.governance-speed__row'),
    )

    if (!rows.length) return

    let frameId: number | null = null

    const updateActiveRow = () => {
      frameId = null

      const triggerPoint = window.innerHeight * 0.6

      let activeRow = rows[0]

      for (const row of rows) {
        const { top } = row.getBoundingClientRect()

        if (top <= triggerPoint) {
          activeRow = row
        } else {
          break
        }
      }

      rows.forEach((row) => {
        row.dataset.active = row === activeRow ? 'true' : 'false'
      })
    }

    const handleScroll = () => {
      if (frameId !== null) return

      frameId = window.requestAnimationFrame(updateActiveRow)
    }

    updateActiveRow()

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [])

  return (
    <>
      <header className="hero product-shot-inline">
        <Stack as="hgroup" gap={4}>
          <h1 className="heading-xxl text-brand-dark">{title}</h1>

          <h2 className="body-l text-brand-dark">
            Metrics, joins, and context in one platform. Governance for your
            team. Guardrails for your agents.
          </h2>
          <Stack row gap={2}>
            <Button to="/demo">Free Trial</Button>
          </Stack>
        </Stack>

        <div className="product-imagery">
          <img
            src="/images/pages/semantic-layer/semantic_layer_hero.webp"
            alt=""
            className="dashboard-image dashboard-image-desktop"
          />
          <img
            src="/images/pages/semantic-layer/semantic_layer_hero_mobile.webp"
            alt=""
            className="dashboard-image dashboard-image-mobile"
          />
        </div>
      </header>

      <section className="page-section page-section--lg semantic-layer">
        <div className=" semantic-layer-inner">
          <hgroup className="centered-section-title">
            <span className="caps-s">The Omni semantic layer</span>
            <h2 className="heading-xl section3-title">
              One model behind every surface
            </h2>
            <p className="body-l text-brand-dark">
              We built Omni around a governed semantic layer.
            </p>
            <p className="body-l text-brand-dark">
              Define a metric in the{' '}
              <a href="/blog/why-ai-needs-a-semantic-model">semantic model</a>,
              and every surface reads from it and returns the same answer,
              whether it's someone using AI or connecting to Omni through
              another tool. Make a change, and every dashboard, agent, and App
              benefits.
            </p>
          </hgroup>

          <div className="semantic-diagram">
            <div className="semantic-diagram__bg" aria-hidden="true" />

            <div className="semantic-diagram__path">
              <svg viewBox="0 0 1235 330" aria-hidden="true">
                <path
                  className="semantic-diagram__stroke"
                  d="M1234.5 617.501C1234.5 276.741 958.261 0.5 617.501 0.5C276.741 0.5 0.5 276.741 0.5 617.501"
                />
              </svg>
            </div>

            <span className="semantic-diagram__pulse" aria-hidden="true" />
            <span
              className="semantic-diagram__pulse semantic-diagram__pulse--2"
              aria-hidden="true"
            />

            <div
              className="semantic-diagram__node"
              style={{ left: '6.98%', top: '59.73%' }}
            >
              <a
                href="https://docs.omni.co/analyze-explore/spreadsheet-tabs"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__icon"
              >
                <img
                  src="/images/pages/semantic-layer/icons/spreadsheets.webp"
                  alt=""
                />
              </a>
              <span className="semantic-diagram__label">Spreadsheets</span>
            </div>

            <div
              className="semantic-diagram__node"
              style={{ left: '18.67%', top: '27.25%' }}
            >
              <a
                href="https://docs.omni.co/visualize-present/dashboards"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__icon"
              >
                <img
                  src="/images/pages/semantic-layer/icons/apps.webp"
                  alt=""
                />
              </a>
              <span className="semantic-diagram__label">
                Dashboards &amp;&nbsp;Apps
              </span>
            </div>

            <div
              className="semantic-diagram__node"
              style={{ left: '32.89%', top: '8.22%' }}
            >
              <a
                href="https://docs.omni.co/analyze-explore"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__icon"
              >
                <img
                  src="/images/pages/semantic-layer/icons/point-click.webp"
                  alt=""
                />
              </a>
              <span className="semantic-diagram__label">Point & click UI</span>
            </div>

            <div
              className="semantic-diagram__node"
              style={{ left: '50%', top: '0%' }}
            >
              <a
                href="https://docs.omni.co/ai"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__icon"
              >
                <img
                  src="/images/pages/semantic-layer/icons/conversational.webp"
                  alt=""
                />
              </a>
              <span className="semantic-diagram__label">
                Conversational Analytics
              </span>
            </div>

            <div
              className="semantic-diagram__node"
              style={{ left: '67.11%', top: '8.22%' }}
            >
              <a href="/embedded-analytics" className="semantic-diagram__icon">
                <img
                  src="/images/pages/semantic-layer/icons/embedded.webp"
                  alt=""
                />
              </a>
              <span className="semantic-diagram__label">
                Embedded Analytics
              </span>
            </div>

            <div
              className="semantic-diagram__node"
              style={{ left: '81.33%', top: '27.25%' }}
            >
              <a
                href="https://docs.omni.co/analyze-explore/sql"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__icon"
              >
                <img src="/images/pages/semantic-layer/icons/sql.webp" alt="" />
              </a>
              <span className="semantic-diagram__label">SQL</span>
            </div>

            <div
              className="semantic-diagram__node"
              style={{ left: '93.02%', top: '59.73%' }}
            >
              <a
                href="https://docs.omni.co/ai/mcp"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__icon"
              >
                <img src="/images/pages/semantic-layer/icons/mcp.webp" alt="" />
              </a>
              <span className="semantic-diagram__label">
                MCP &amp;
                <br />
                APIs
              </span>
            </div>

            <div className="semantic-diagram__stack">
              <a
                href="https://docs.omni.co/modeling/models/ai-context"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__pill semantic-diagram__pill--ai"
              >
                <strong>AI context</strong>
                <span>
                  Teaches AI about your business · Captures your team's
                  knowledge
                </span>
              </a>

              <UpDownArrowsIcon showPulses />

              <a
                href="https://docs.omni.co/modeling"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__pill semantic-diagram__pill--semantic"
              >
                <strong>Semantic layer</strong>
                <span>
                  Metric definitions, business context, security rules
                </span>
              </a>

              <UpDownArrowsIcon showPulses dotColor="dark" pulseOffset={0.5} />

              <a
                href="https://docs.omni.co/connect-data"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram__pill semantic-diagram__pill--warehouse"
              >
                <strong>Data warehouse</strong>
              </a>
            </div>
          </div>

          <div className="semantic-diagram-mobile">
            <div className="semantic-diagram-mobile__nodes">
              <div className="semantic-diagram-mobile__tier">
                <MobileArc />

                <div className="semantic-diagram-mobile__node semantic-diagram-mobile__node--connector-tall">
                  <a
                    href="https://docs.omni.co/ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="semantic-diagram-mobile__icon"
                  >
                    <img
                      src="/images/pages/semantic-layer/icons/conversational.webp"
                      alt=""
                    />
                  </a>
                  <span className="semantic-diagram-mobile__label">
                    Conversational Analytics
                  </span>
                </div>

                <div className="semantic-diagram-mobile__row">
                  <div className="semantic-diagram-mobile__node semantic-diagram-mobile__node--connector">
                    <a
                      href="https://docs.omni.co/analyze-explore"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="semantic-diagram-mobile__icon"
                    >
                      <img
                        src="/images/pages/semantic-layer/icons/point-click.webp"
                        alt=""
                      />
                    </a>
                    <span className="semantic-diagram-mobile__label">
                      Point &amp; click UI
                    </span>
                  </div>

                  <div className="semantic-diagram-mobile__node semantic-diagram-mobile__node--connector">
                    <a
                      href="https://docs.omni.co/analyze-explore/sql"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="semantic-diagram-mobile__icon"
                    >
                      <img
                        src="/images/pages/semantic-layer/icons/sql.webp"
                        alt=""
                      />
                    </a>
                    <span className="semantic-diagram-mobile__label">SQL</span>
                  </div>
                </div>
              </div>

              <div className="semantic-diagram-mobile__tier">
                <div className="semantic-diagram-mobile__node semantic-diagram-mobile__node--embedded">
                  <a
                    href="https://omni.co/embedded-analytics"
                    className="semantic-diagram-mobile__icon"
                  >
                    <img
                      src="/images/pages/semantic-layer/icons/embedded.webp"
                      alt=""
                    />
                  </a>
                  <span className="semantic-diagram-mobile__label">
                    Embedded Analytics
                  </span>
                </div>

                <div className="semantic-diagram-mobile__row">
                  <div className="semantic-diagram-mobile__node semantic-diagram-mobile__node--connector-stack-long">
                    <a
                      href="https://docs.omni.co/visualize-present/dashboards"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="semantic-diagram-mobile__icon"
                    >
                      <img
                        src="/images/pages/semantic-layer/icons/apps.webp"
                        alt=""
                      />
                    </a>
                    <span className="semantic-diagram-mobile__label">
                      Dashboards &amp;&nbsp;Apps
                    </span>
                  </div>

                  <div
                    className="semantic-diagram-mobile__node semantic-diagram-mobile__node--connector-stack-long"
                    style={{ '--pulse-delay': '-1.6s' } as CSSProperties}
                  >
                    <a
                      href="https://docs.omni.co/ai/mcp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="semantic-diagram-mobile__icon"
                    >
                      <img
                        src="/images/pages/semantic-layer/icons/mcp.webp"
                        alt=""
                      />
                    </a>
                    <span className="semantic-diagram-mobile__label">
                      MCP &amp;
                      <br />
                      APIs
                    </span>
                  </div>
                </div>
              </div>

              <div className="semantic-diagram-mobile__node semantic-diagram-mobile__node--connector-stack">
                <a
                  href="https://docs.omni.co/analyze-explore/spreadsheet-tabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="semantic-diagram-mobile__icon"
                >
                  <img
                    src="/images/pages/semantic-layer/icons/spreadsheets.webp"
                    alt=""
                  />
                </a>
                <span className="semantic-diagram-mobile__label">
                  Spreadsheets
                </span>
              </div>
            </div>

            <div className="semantic-diagram-mobile__stack">
              <a
                href="https://docs.omni.co/modeling/models/ai-context"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram-mobile__pill semantic-diagram-mobile__pill--ai"
              >
                <strong>AI context</strong>
                <span>
                  Teaches AI about your business · Captures your team's
                  knowledge
                </span>
              </a>

              <UpDownArrowsIcon
                className="semantic-diagram-mobile__arrows"
                showPulses
              />

              <a
                href="https://docs.omni.co/modeling"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram-mobile__pill semantic-diagram-mobile__pill--semantic"
              >
                <strong>Semantic layer</strong>
                <span>
                  Metric definitions, business context, security rules
                </span>
              </a>

              <UpDownArrowsIcon
                className="semantic-diagram-mobile__arrows"
                showPulses
                dotColor="dark"
                pulseOffset={0.5}
              />

              <a
                href="https://docs.omni.co/connect-data"
                target="_blank"
                rel="noopener noreferrer"
                className="semantic-diagram-mobile__pill semantic-diagram-mobile__pill--warehouse"
              >
                <strong>Data warehouse</strong>
              </a>
            </div>
          </div>
        </div>
      </section>

      <CustomersMarquee title="Trusted by data teams at" mobileGrid={false} />

      <section className="page-section page-section--lg trust-rows">
        <div className="trust-rows-inner">
          <h2 className="heading-xl text-brand-dark">
            How our semantic layer builds trust
          </h2>
          <p className="body-l text-brand-dark">
            Giving you governance without giving up speed.
          </p>

          <span className="caps-s">Omni's semantic layer is:</span>

          <ul className="trust-rows__list">
            {TRUST_ROWS.map(({ id, label, iconSrc, description }) => (
              <li key={id} className="trust-rows__row">
                <a
                  href={`#${id}`}
                  className="trust-rows__row-link"
                  aria-hidden="true"
                  tabIndex={-1}
                />

                <span className="trust-rows__icon">
                  <img src={iconSrc} alt="" aria-hidden="true" />
                </span>

                <span className="trust-rows__label heading-s text-brand-dark">
                  {label}
                  <ChevronRight aria-hidden="true" />
                </span>

                <p className="trust-rows__description body-m text-brand-dark">
                  {description}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <TabAccordion
        name="model-as-you-go"
        id="flexible"
        heading={
          <>
            <span className="caps-s model-as-you-go-overline">
              <img
                className="model-as-you-go-overline__icon"
                src="/images/pages/semantic-layer/icons/flexible.png"
                alt=""
                aria-hidden="true"
              />
              Flexible
            </span>
            Model as you go
          </>
        }
        subheading="Building the semantic layer doesn't require a heavy upfront effort: start querying the moment you connect your database. Add and refine context as your business evolves, and manage model changes like code."
        items={MODEL_AS_YOU_GO_ITEMS}
        showNavArrows
        navArrowsVariant="carousel"
        fullWidthHeading
      />

      <section className="synthesia-callout">
        <CallOutList alternateSectionDirection={false}>
          <CallOutItem>
            <CallOutIllustration>
              <img
                src="/images/pages/semantic-layer/synthesia_data_stack.webp"
                alt="Diagram showing Synthesia's data stack: workbooks flowing into the semantic layer, then into warehouse tables"
              />
            </CallOutIllustration>

            <CallOutText>
              <hgroup>
                <h4 className="heading-l">
                  How Synthesia democratized data modeling with Omni
                </h4>
                <p>
                  Business users at Synthesia define new metrics in Omni's UI,
                  and the data team reviews and promotes them through the
                  semantic layer. Expertise lands in the model without losing
                  consistency.
                </p>
                <Button variant="secondary" to="/blog/case-study-synthesia">
                  Read the case study
                </Button>
              </hgroup>
            </CallOutText>
          </CallOutItem>
        </CallOutList>

        <QuoteCarousel quotes={[QuoteSynthesiaModeling]} />
      </section>

      <section
        id="interoperable"
        className="page-section page-section--lg existing-investments"
      >
        <div className="page-section__inner">
          <CallOutList>
            <hgroup className="section-header">
              <span className="caps-s existing-investments-overline">
                <img
                  className="existing-investments-overline__icon"
                  src="/images/pages/semantic-layer/icons/interoperable.png"
                  alt=""
                  aria-hidden="true"
                />
                Interoperable
              </span>
              <h3 className="heading-xxl text-brand-dark">
                Built for your existing investments
              </h3>
              <p className="body-l text-brand-dark">
                Omni's model is written in YAML, and committed to{' '}
                <a href="/blog/announcing-the-open-semantic-interchange">
                  open semantics
                </a>{' '}
                through <a href="https://ossie.apache.org/">Apache Ossie</a>.
                Your data stays in your warehouse, logic stays
                version-controlled in Git, and integrations run in both
                directions, giving you a shared interface for your data stack.
              </p>
            </hgroup>

            <CallOutItem>
              <h4 className="callout-heading heading-m">
                Maintain a single source of truth
              </h4>
              <CallOutText>
                <hgroup>
                  <p className="body-l">
                    The <a href="/dbt">dbt integration</a> is bi-directional.
                    View dbt metadata in Omni, author models from Omni queries,
                    and push logic upstream. Branch mode isolates the change,
                    and git keeps the history.
                  </p>
                  <p className="body-l">
                    Snowflake Semantic Views and Databricks Unity Catalog Metric
                    Views can be imported and queried directly. Explore, add AI
                    context, and version them in Omni, then push definitions
                    back out with Omni's open source{' '}
                    <a
                      href="https://docs.omni.co/developers/agent-skills"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      agent skills
                    </a>
                    .
                  </p>
                  <p className="body-l">
                    Native connections support Snowflake, Databricks,
                    ClickHouse, and more.
                  </p>
                </hgroup>
              </CallOutText>
              <CallOutIllustration>
                <img
                  src="/images/pages/semantic-layer/source-of-truth-diagram.webp"
                  alt="Omni's semantic layer syncing with dbt, Snowflake, and Databricks over Git"
                />
              </CallOutIllustration>
            </CallOutItem>

            <CallOutItem>
              <h4 className="callout-heading heading-m">Universal semantics</h4>
              <CallOutText>
                <hgroup>
                  <p className="body-l">
                    Our semantic layer works across the tools you already use,
                    even when you're not in Omni.
                  </p>
                  <p className="body-l">
                    Definitions, permissions, and context are enforced on every
                    query. Access governed metrics in{' '}
                    <a href="/blog/announcing-claude-connector-and-claude-skills">
                      Claude
                    </a>
                    ,{' '}
                    <a
                      href="https://docs.omni.co/ai/mcp/chatgpt"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ChatGPT
                    </a>
                    ,{' '}
                    <a
                      href="https://docs.omni.co/integrations/omni-slack-agent"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Slack
                    </a>
                    ,{' '}
                    <a
                      href="https://docs.omni.co/ai/mcp/cursor"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cursor
                    </a>
                    , via{' '}
                    <a
                      href="https://docs.omni.co/ai/mcp"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      MCP
                    </a>
                    ,{' '}
                    <a
                      href="https://docs.omni.co/api"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      API
                    </a>
                    ,{' '}
                    <a
                      href="https://docs.omni.co/developers/cli"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      CLI
                    </a>
                    , or{' '}
                    <a href="/embedded-analytics">
                      embed them in custom applications
                    </a>
                    .
                  </p>
                </hgroup>
              </CallOutText>
              <CallOutIllustration className="universal-semantics-diagram">
                <img
                  src="/images/pages/semantic-layer/universal-semantics-diagram.svg"
                  alt="Definitions, permissions, and context from Omni's shared model enforced across tools like Cursor, Claude, ChatGPT, and Slack"
                />
              </CallOutIllustration>
            </CallOutItem>
          </CallOutList>
        </div>
      </section>

      <section
        id="extensible"
        className="page-section page-section--lg extend-model"
      >
        <div className="page-section__inner">
          <CallOutList>
            <hgroup className="section-header">
              <span className="caps-s extend-model-overline">
                <img
                  className="extend-model-overline__icon"
                  src="/images/pages/semantic-layer/icons/extensible.png"
                  alt=""
                  aria-hidden="true"
                />
                Extensible
              </span>
              <h3 className="heading-xxl text-brand-dark">
                Extend the model, don't fork it
              </h3>
              <p className="body-l text-brand-dark">
                Every company has exceptions. Regional rules, team-specific
                metrics, unique tenant needs. When the semantic layer can't hold
                them, people copy the model and route around it. Numbers drift,
                answers conflict. Omni's hub-and-spoke model handles the
                exceptions, while core definitions stay intact.
              </p>
            </hgroup>

            <CallOutItem>
              <h4 className="callout-heading heading-m">
                Build on what you already have
              </h4>
              <CallOutText>
                <hgroup>
                  <p className="body-l">
                    A central hub model holds the shared logic. A department or
                    tenant creates a{' '}
                    <a
                      href="https://docs.omni.co/modeling/views/parameters/extends"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      spoke
                    </a>{' '}
                    that inherits all of it, and adds or overrides only what's
                    different. Unique logic, permissions, and styling are
                    handled without duplicating the base model.
                  </p>
                </hgroup>
              </CallOutText>
              <CallOutIllustration>
                <img
                  src="/images/pages/semantic-layer/hub-and-spoke-diagram.webp"
                  alt="A core model connected to spokes A, B, and C, sharing code, customizations, and user attributes"
                />
              </CallOutIllustration>
            </CallOutItem>

            <CallOutItem>
              <h4 className="callout-heading heading-m">
                Scale the model, not the maintenance
              </h4>
              <CallOutText>
                <hgroup>
                  <p className="body-l">
                    Adding a region or tenant means adding a spoke, not a new
                    model or duplicate dashboards.{' '}
                    <a
                      href="https://docs.omni.co/modeling/models/dynamic-shared-extensions"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Dynamic shared extensions
                    </a>{' '}
                    read each viewer's user attributes and route them to the
                    right spoke automatically.
                  </p>
                  <p className="body-l">
                    Each person sees their own metrics and only the data they're
                    allowed to see. Domain-level permissions control visibility,
                    and Git followers keep every deployment on the same version
                    of the model.
                  </p>
                </hgroup>
              </CallOutText>
              <CallOutIllustration>
                <img
                  src="/images/pages/semantic-layer/scale-model-diagram.webp"
                  alt="Region, tenant, and team viewers routed by attributes to one dashboard and one model, then to region schema, embedded tenant, and department rules"
                />
              </CallOutIllustration>
            </CallOutItem>
          </CallOutList>
        </div>
      </section>

      <TabAccordion
        name="teach-ai-your-business"
        id="comprehensive"
        heading={
          <>
            <span className="caps-s teach-ai-overline">
              <img
                className="teach-ai-overline__icon"
                src="/images/pages/semantic-layer/icons/Two-Color.png"
                alt=""
                aria-hidden="true"
              />
              Comprehensive
            </span>
            Teach AI your business
          </>
        }
        subheading={
          <>
            A semantic layer that only holds metric definitions{' '}
            <a href="/blog/what-makes-a-semantic-layer-good">
              invites data sprawl
            </a>
            . The rest of the logic ends up in Excel exports and SQL that drifts
            from the source. Omni&apos;s semantic model combines the
            definitions, join paths, and context your team uses.
          </>
        }
        items={TEACH_AI_ITEMS}
        showNavArrows
        fullWidthHeading
      />

      <QuoteCarousel quotes={[QuoteCheckr]} tone="green" reverse />

      <section
        id="governable"
        className="page-section page-section--lg governance-speed"
        ref={governanceSectionRef}
      >
        <div className="page-section__inner governance-speed__inner">
          <header className="governance-speed__header">
            <div className="governance-speed__eyebrow caps-s">
              <span
                className="governance-speed__eyebrow-icon"
                aria-hidden="true"
              >
                <img
                  src="/images/pages/semantic-layer/icons/governable.png"
                  alt=""
                />
              </span>
              <span>Governable</span>
            </div>
            <h2 className="heading-xxl">
              Governance and speed, without the trade-off
            </h2>
            <p>
              Your skepticism about AI on company data is earned. AI by itself
              doesn't fix chaos, it{' '}
              <a href="/blog/why-text-to-sql-fails">amplifies it</a>. Omni fixes
              this: every question, from every surface, runs through one
              governed model, and returns a reliable answer. And that governance
              doesn't cost you speed.
            </p>
          </header>

          <div className="governance-speed__rows">
            <article className="governance-speed__row" data-active="true">
              <h3 className="governance-speed__row-title heading--m">
                Same question, same answer
              </h3>

              <div className="governance-speed__visual">
                <img
                  src="/images/pages/semantic-layer/two-answer-readout.webp"
                  alt="Dashboard and AI chat returning the same governed answer"
                />
              </div>

              <div className="governance-speed__row-copy">
                <p>
                  Whether the question comes from a dashboard, workbook, or AI
                  chat, it returns the{' '}
                  <a
                    className="governance-speed__link"
                    href="/blog/benchmarking-omnis-agentic-analytics-harness"
                  >
                    one answer
                  </a>{' '}
                  your team defined. Access filters enforce row-level security,
                  and access grants control columns and datasets. That
                  consistency earns trust.
                </p>
              </div>
            </article>

            <article className="governance-speed__row">
              <h3 className="governance-speed__row-title heading--m">
                It's in the harness
              </h3>

              <div className="governance-speed__visual">
                <img
                  src="/images/pages/semantic-layer/guardrails-topic-gate.webp"
                  alt="Omni guardrails routing questions through governed topics and access controls"
                />
              </div>

              <div className="governance-speed__row-copy">
                <p>
                  The fix for{' '}
                  <a
                    className="governance-speed__link"
                    href="/blog/why-text-to-sql-fails"
                  >
                    drifting answers
                  </a>{' '}
                  is architectural, not a better prompt or AI model. Omni's AI{' '}
                  <a
                    className="governance-speed__link"
                    href="/blog/benchmarking-omnis-agentic-analytics-harness"
                  >
                    harness
                  </a>{' '}
                  enforces guardrails: a question routes to one Topic, the agent
                  selects fields and filters, and our query engine compiles the
                  SQL.
                </p>
              </div>
            </article>

            <article className="governance-speed__row">
              <h3 className="governance-speed__row-title heading--m">
                A secure platform underneath
              </h3>

              <div className="governance-speed__visual">
                <img
                  src="/images/pages/semantic-layer/five-icon-row.webp"
                  alt=""
                />
              </div>

              <div className="governance-speed__row-copy">
                <p>
                  The model governs what people see. The platform protects the
                  rest. Omni is{' '}
                  <a
                    className="governance-speed__link"
                    href="/blog/omni-achieves-iso-27001-certification"
                  >
                    ISO 27001 certified
                  </a>{' '}
                  and SOC 2 Type II audited annually and meets GDPR, CCPA, and
                  HIPAA standards. Customer data is encrypted with AES-256 at
                  rest and TLS in transit, and SAML controls sign-on.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <QuoteCarousel
        heading="Why customers choose Omni's semantic layer"
        quotes={[
          QuoteBuzzfeed3,
          QuoteHeidi,
          QuoteSynthesia2,
          QuoteCheckr2,
          QuoteBrevo,
          QuoteGuitarCenter,
          QuoteCribl3,
          QuoteUscreen4,
        ]}
      />

      <section className="page-section page-section--lg section-spacing post-collection-section read-more">
        <div className="page-section__inner">
          <PostCollection
            header={
              <hgroup>
                <h2 className="heading-xl text-brand-dark">
                  Why AI context matters
                </h2>
              </hgroup>
            }
            posts={posts}
            variant="carousel"
          />
        </div>
      </section>
    </>
  )
}
